/**
 * PWA install prompt manager — one place where the captured
 * `beforeinstallprompt` event lives so any component (InstallButton,
 * ShareButton's "Add to Home Screen" entry, etc.) can fire the native
 * browser install dialog.
 *
 * The `beforeinstallprompt` event only fires once per page load, so a
 * single module-level listener (attached on import) is more reliable
 * than per-component listeners that might mount after the event has
 * already fired.
 *
 * The real browser `appinstalled` event also only fires once per
 * install, but multiple components (InstallButton is mounted in
 * SiteActionBar, HomeUtilityBar and Footer at once) used to each attach
 * their own listener and independently ping the analytics endpoint —
 * turning one real install into several logged rows. The single
 * module-level listener below is the only place that pings the backend,
 * so one install always logs exactly one row no matter how many
 * InstallButton instances happen to be mounted.
 */

import { trackEvent } from "./analytics";
import { api } from "./api";

let deferredEvent = null;
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch (_) { /* ignore */ }
  }
}

// Best-effort detection of the visitor's OS, sent with the install ping so
// the admin can see installs split by platform. Done off the UA string
// because `navigator.userAgentData.platform` isn't on Safari yet.
function detectPlatform() {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/i.test(ua)) return "ios";
  if (/Windows/i.test(ua)) return "windows";
  if (/Mac OS X/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  return "other";
}

function pingInstall() {
  // One ping per accepted install, ever — failures are silent, analytics
  // must never break the install flow.
  try {
    let session = "";
    try {
      const raw = localStorage.getItem("last_analytics_sid");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.sid) session = parsed.sid;
      }
    } catch (_) { /* ignore */ }
    api.post("/analytics/install", { session, platform: detectPlatform() }).catch(() => {});
  } catch (_) { /* ignore */ }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredEvent = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    pingInstall();
    notify();
  });
}

export function hasNativeInstallPrompt() {
  return deferredEvent != null;
}

/**
 * Subscribe to install-prompt availability changes. Returns the
 * unsubscribe fn. Used by React hooks so components can re-render when
 * the native prompt becomes available / gets consumed.
 */
export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Fire the native install dialog if the browser captured one. Returns
 * the outcome ("accepted" / "dismissed" / "unsupported"). When
 * unsupported (iOS Safari, Firefox desktop, in-app browsers, etc.) the
 * caller is expected to show its own instructional modal instead.
 */
export async function triggerInstallPrompt() {
  if (!deferredEvent) return "unsupported";
  try {
    deferredEvent.prompt();
    const choice = await deferredEvent.userChoice;
    deferredEvent = null;
    notify();
    const outcome = choice?.outcome || "dismissed";
    trackEvent("pwa_install_prompt_outcome", { outcome });
    return outcome;
  } catch (_) {
    deferredEvent = null;
    notify();
    return "dismissed";
  }
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  } catch (_) { /* ignore */ }
  return !!window.navigator?.standalone;
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

// React-friendly subscription that returns whether the native install
// dialog is currently available. Components that need to render
// differently based on prompt availability can `useInstallReady()` and
// will re-render when the event is captured / consumed.
import { useEffect, useState } from "react";

export function useInstallReady() {
  const [ready, setReady] = useState(hasNativeInstallPrompt());
  useEffect(() => {
    const sync = () => setReady(hasNativeInstallPrompt());
    sync();
    return subscribeInstallPrompt(sync);
  }, []);
  return ready;
}
